import React, { useEffect, useMemo } from 'react'
import { useParams } from 'react-router'

import { useEditor } from '@toasttab/sites-components'
import classnames from 'classnames'
import { t } from 'src/translations'

import { Cta, CtaType, LogoSizeOption } from 'src/apollo/sites'
import { isPreviewSite } from 'src/lib/js/previewModeUtils'
import { useStaticSiteContext } from 'src/public/components/app/StaticSiteContext'
import { MenuGrouping } from 'src/apollo/onlineOrdering'
import {
  RestaurantSiteContent,
  useRestaurant
} from 'shared/components/common/restaurant_context/RestaurantContext'
import { useScrollNavBehavior } from 'src/lib/js/hooks/useScrollNavBehavior'

import Footer from 'public/components/default_template/footer'
import Nav from 'public/components/default_template/nav/Nav'
import PageMeta from 'public/components/default_template/meta/PageMeta'
import { useOOMenus } from 'public/components/online_ordering/useOOMenu'

type Params = {
  menuGuid?: string
  groupGuid?: string
  itemSlug?: string
  guid?: string
  groupSlug?: string
  variationSlug?: string
}

export type EcommercePageLayoutChildProps = {
  menuGuid?: string
  groupGuid?: string
  itemSlug?: string
  itemGuid?: string
  itemGroupGuid?: string
  variationSlug?: string
  groupings?: MenuGrouping[] | null
  hideHero?: boolean
  scrollNavRef?: React.Ref<HTMLDivElement>
  hasHero?: boolean
}

type Props = {
  children:
    | ((props: EcommercePageLayoutChildProps) => React.ReactElement)
    | React.ReactElement
}

/**
 * Layout component for ecommerce pages that provides Nav, Footer, and page structure
 * without providing context providers (those are handled by EcommerceProvidersWrapper)
 */
const EcommercePageLayoutInner = ({
  children,
  params
}: Props & { params: Params }) => {
  const {
    restaurant: siteRestaurant,
    selectedLocation,
    ooRestaurant: restaurant,
    toastProduct
  } = useRestaurant()

  const staticContext = useStaticSiteContext()
  const { scrollNavRef } = useScrollNavBehavior()

  useEffect(() => {
    require('smoothscroll-polyfill').polyfill()
  }, [])

  const { groupings } = useOOMenus({
    skipPopularItems: true
  })

  const { title, description } = useMemo(() => {
    const title =
      selectedLocation?.orderPageConfigOverride?.seoMeta?.title ??
      siteRestaurant.config.ooConfig?.seoMeta?.title ??
      t('order-page-wrapper.order-online') ??
      undefined
    const description: string | undefined =
      selectedLocation?.orderPageConfigOverride?.seoMeta?.description ??
      siteRestaurant.config.ooConfig?.seoMeta?.description ??
      undefined
    return { title, description }
  }, [
    selectedLocation?.orderPageConfigOverride?.seoMeta?.description,
    siteRestaurant.config.ooConfig?.seoMeta?.description,
    selectedLocation?.orderPageConfigOverride?.seoMeta?.title,
    siteRestaurant.config.ooConfig?.seoMeta?.title
  ])

  const { content } = siteRestaurant
  const usesOOBasic = siteRestaurant?.config?.hasFullCustomization === false

  const { primaryCta, nonPrimaryCtas } = content as RestaurantSiteContent
  const giftCardCTA: Cta = {
    type: CtaType.Link,
    text: t('order-page-wrapper.buy-gift-cards'),
    link: restaurant?.giftCardLinks?.purchaseLink
  }
  const newNonPrimaryCtas =
    restaurant?.giftCardLinks?.purchaseLink && usesOOBasic
      ? [...(nonPrimaryCtas || []), giftCardCTA]
      : nonPrimaryCtas

  const childProps: EcommercePageLayoutChildProps = {
    menuGuid: params.menuGuid,
    groupGuid: params.groupGuid,
    itemSlug: params.itemSlug,
    itemGuid: params.itemSlug?.split('_')?.[1] ?? params.guid,
    itemGroupGuid: params.groupSlug?.split('_')?.[1] ?? params.guid,
    variationSlug: params.variationSlug,
    groupings,
    scrollNavRef,
    hideHero: false // Default to showing hero; can be overridden by future enhancements
  }

  return (
    <>
      <PageMeta title={title} description={description} />
      <div className={classnames('defaultTemplate', 'ecommPage')}>
        <Nav
          withShadow={false}
          logoSrc={siteRestaurant.meta?.icon}
          logoObject={siteRestaurant.meta?.iconObject}
          logoSize={LogoSizeOption.Standard}
          navType='stickyNav'
          primaryCta={primaryCta}
          nonPrimaryCtas={
            isPreviewSite(staticContext, siteRestaurant.config, toastProduct)
              ? []
              : newNonPrimaryCtas
          }
          forceDisplayToastLogin
          withCart
          withOOOptions
          shouldShowPreviewBanner={false}
        />
        {typeof children === 'function' ? children(childProps) : children}
        <Footer></Footer>
      </div>
    </>
  )
}

/**
 * Component that extracts route params - only used in non-editor mode
 */
const ParamDetectingEcommercePageLayout = (props: Props) => {
  const params = useParams<Params>()
  return <EcommercePageLayoutInner {...props} params={params} />
}

/**
 * Wrapper that handles param extraction based on editor context.
 * In editor mode (sites-admin-spa), there's no router context, so we use empty params.
 * In non-editor mode, we extract params from the route.
 */
export const EcommercePageLayout = (props: Props) => {
  const { isEditor } = useEditor()

  return isEditor ? (
    <EcommercePageLayoutInner {...props} params={{}}>
      {props.children}
    </EcommercePageLayoutInner>
  ) : (
    <ParamDetectingEcommercePageLayout {...props}>
      {props.children}
    </ParamDetectingEcommercePageLayout>
  )
}
